import { useCallback, useState } from "react";

import { useAuthStore } from "~/store/auth-store";
import type { UserUpdate } from "~/types/auth";

export type SaveStatus = "idle" | "saved" | "failed";

export interface ProfileSave {
  /** Persist a partial update. Never throws — inspect `status` instead. */
  save: (update: UserUpdate) => Promise<void>;
  saving: boolean;
  status: SaveStatus;
  dismiss: () => void;
}

/**
 * Persisting one field of the member's profile, with the outcome surfaced.
 *
 * Every settings sub-screen writes through this rather than calling the store directly,
 * so none of them can quietly swallow a failure — a settings screen that looks like it
 * saved but did not is worse than one that says it failed.
 *
 * A 401 needs no handling here: the API client broadcasts it and `AuthGate` drops the
 * whole session to the sign-in screen.
 */
export function useProfileSave(): ProfileSave {
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<SaveStatus>("idle");

  const save = useCallback(
    async (update: UserUpdate) => {
      setSaving(true);
      setStatus("idle");
      try {
        await updateProfile(update);
        setStatus("saved");
      } catch {
        setStatus("failed");
      } finally {
        setSaving(false);
      }
    },
    [updateProfile],
  );

  const dismiss = useCallback(() => setStatus("idle"), []);

  return { save, saving, status, dismiss };
}
